import { useLazyQuery } from "@apollo/client";
import { useCallback, useContext, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  hasStepsWithCategoryBasedRules,
  reconstructSurveyOutcomes,
} from "../components/campaign/campaignUtils";
import { RETAILER_RULE_SLUG } from "../components/campaign/rulesAndOutcomes/outcomes/retailerOutcome";
import { STORE_GROUP_RULE_SLUG } from "../components/campaign/rulesAndOutcomes/outcomes/storeOutcome";
import { CampaignSetupContext } from "../context/CampaignSetupContext";
import LoaderContext from "../context/LoaderContext";
import { loadCampaignById } from "../graphql/queries/loadCampaignById";
import { normalizeEmailPreferences } from "./useCampaignEmailConfiguration";
import useRewardGroupsHandlerHook from "./useRewardGroupsHandlerHook";

// Shared helper to normalize excludeSegments vs exclusion rule
export const normalizeExcludeSegments = (
  campaignObject,
  getSegmentIdsFromRuleFn
) => {
  if (!campaignObject) return campaignObject;

  // Ensure excludeSegments is an array; if missing, derive from rule (for load flow)
  if (!Array.isArray(campaignObject.excludeSegments) && getSegmentIdsFromRuleFn) {
    campaignObject.excludeSegments = getSegmentIdsFromRuleFn(
      campaignObject?.campaignRules,
      "exclusion-segment"
    );
  }

  // If excludeSegments exist but the exclusion rule is missing, drop excludeSegments
  if (
    Array.isArray(campaignObject.excludeSegments) &&
    campaignObject.excludeSegments.length > 0
  ) {
    const hasExclusionRule = campaignObject.campaignRules?.some(
      (rule) =>
        rule?.ruleDefinition?.slug === "exclusion-segment" ||
        rule?.ruleDefinitionSlug === "exclusion-segment"
    );

    if (!hasExclusionRule) {
      campaignObject.excludeSegments = [];
    }
  }

  return campaignObject;
};

export const clearInvalidConditionalOutcomeRules = (newData, prevState) => {
  const asArray = (v) => (Array.isArray(v) ? v : []);

  const newRetailers = asArray(newData?.retailers);
  const prevRetailers = asArray(prevState?.retailers);
  const retailersChanged =
    JSON.stringify(prevRetailers) !== JSON.stringify(newRetailers);

  const newStoreGroups = asArray(newData?.store_groups ?? newData?.storeGroups);
  const prevStoreGroups = asArray(prevState?.store_groups ?? prevState?.storeGroups);
  const storeGroupsChanged =
    JSON.stringify(prevStoreGroups) !== JSON.stringify(newStoreGroups);

  if (!retailersChanged && !storeGroupsChanged) {
    return newData;
  }

  const newRetailersSet = new Set(newRetailers);
  const newStoreGroupsSet = new Set(newStoreGroups);

  const isRetailerRuleInvalid = (ruleData) => {
    const keywords = asArray(ruleData?.keywords);
    return keywords.some((kw) => !newRetailersSet.has(kw));
  };

  const isStoreGroupRuleInvalid = (ruleData) => {
    const category = ruleData?.store_configuration?.category;
    return !!category && !newStoreGroupsSet.has(category);
  };

  const clearOutcomeRules = (outcomes) => {
    if (!Array.isArray(outcomes)) return outcomes;
    return outcomes.map((outcome) => {
      const rules = outcome?.outcomeRules;
      if (!Array.isArray(rules)) return outcome;

      const updatedRules = rules.map((rule) => {
        const slug = rule?.ruleDefinition?.slug;
        if (
          retailersChanged &&
          slug === RETAILER_RULE_SLUG &&
          isRetailerRuleInvalid(rule.ruleData)
        ) {
          return { ...rule, ruleData: {} };
        }
        if (
          storeGroupsChanged &&
          slug === STORE_GROUP_RULE_SLUG &&
          isStoreGroupRuleInvalid(rule.ruleData)
        ) {
          return { ...rule, ruleData: {} };
        }
        return rule;
      });
      const changed = updatedRules.some((rule, i) => rule !== rules[i]);

      return changed ? { ...outcome, outcomeRules: updatedRules } : outcome;
    });
  };

  const updatedData = { ...newData };

  if (updatedData.outcomes) {
    updatedData.outcomes = clearOutcomeRules(updatedData.outcomes);
  }

  if (Array.isArray(updatedData.activities)) {
    updatedData.activities = updatedData.activities.map((activity) => {
      if (!activity?.outcomes) return activity;
      return { ...activity, outcomes: clearOutcomeRules(activity.outcomes) };
    });
  }

  return updatedData;
};

export const useCampaignStateHandlerHook = () => {
  const { setCampaignState, campaignState, currentCampaignTypeConfig } =
    useContext(CampaignSetupContext);
  const { setIsLoading } = useContext(LoaderContext);
  const [loadCampaign] = useLazyQuery(loadCampaignById);
  const { copyRewardGroupOutcomes } = useRewardGroupsHandlerHook();
  const params = useParams();

  const categoryActivityRulesSetup = hasStepsWithCategoryBasedRules(
    currentCampaignTypeConfig
  );

  const fetchCampaignData = useCallback(
    async (campaignId) => {
      try {
        setIsLoading(true);
        const result = await loadCampaign({ variables: { id: campaignId } });
        const campaignData = result?.data?.loadCampaignById;

        if (campaignData?.id) {
          const loaddata = processCampaignData(campaignData);
          if (loaddata?.outcomes?.length) {
            const newOutcomes = await copyRewardGroupOutcomes(
              loaddata.outcomes,
              loaddata?.id,
              "edit"
            );
            loaddata.outcomes = newOutcomes;
          }
          setCampaignState(loaddata);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadCampaign, setIsLoading, setCampaignState]
  );

  const getSegmentIdsFromRule = (rules, targetSlug) => {
    if (!Array.isArray(rules)) {
      return [];
    }

    const rule = rules.find(
      (ruleItem) =>
        ruleItem?.ruleDefinition?.slug === targetSlug ||
        ruleItem?.ruleDefinitionSlug === targetSlug
    );

    return Array.isArray(rule?.ruleData?.segment_ids)
      ? [...rule.ruleData.segment_ids]
      : [];
  };

  const processCampaignData = (campaignData) => {
    const clonedData = JSON.parse(JSON.stringify(campaignData));
    delete clonedData.__typename;
    clonedData.store_groups = clonedData.storeGroups || undefined;

    const contentDefinitionSlug = getContentDefinitionSlug();
    if (clonedData.campaignContent) {
      clonedData.campaignContent.contentDefinitionSlug = contentDefinitionSlug;
    }

    normalizeExcludeSegments(clonedData, getSegmentIdsFromRule);

    // Normalize emailPreferences so loaded campaigns always have the expected

    if (clonedData) {
      clonedData.emailPreferences = normalizeEmailPreferences(
        clonedData.emailPreferences
      );
    }

    return reconstructSurveyOutcomes(clonedData);
  };

  const getContentDefinitionSlug = () => {
    return currentCampaignTypeConfig.steps
      ?.find((step) => step.slug === "generic-setup")
      .values?.find((value) => value.type === "content").definition;
  };

  useEffect(() => {
    if (params.campaignId && !campaignState.type) {
      fetchCampaignData(params.campaignId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.campaignId, fetchCampaignData]);

  const handleCampaignSetup = (data) =>
    setCampaignState((prev) => clearInvalidConditionalOutcomeRules(data, prev));

  const handleCampaignContent = (data, contentDefinitionSlug) => {
    setCampaignState((prev) => {
      const wasReordering =
        prev?.campaignContent?.contentData?.enable_activity_reordering;
      const nowReordering = data?.enable_activity_reordering;
      const shouldResequence = nowReordering === true && !wasReordering;

      return {
        ...prev,
        campaignContent: {
          contentDefinitionSlug,
          ...prev.campaignContent,
          contentData: { ...prev?.campaignContent?.contentData, ...data },
        },
        activities: shouldResequence
          ? prev.activities.map((activity, idx) => ({
              ...activity,
              activityData: { ...activity.activityData, sequence_order: idx + 1 },
            }))
          : prev.activities,
      };
    });
  };

  const handleTermsContent = (termsContent) => {
    setCampaignState((prev) => ({
      ...prev,
      campaignContent: {
        ...prev.campaignContent,
        contentData: {
          ...prev.campaignContent.contentData,
          terms_and_conditions: termsContent?.terms_and_conditions?.length
            ? termsContent.terms_and_conditions
            : undefined,
        },
      },
    }));
  };

  const presetJSONData = (inputSchema) => {
    const data = {};
    if (inputSchema && Object.keys(inputSchema).length) {
      for (const key in inputSchema.properties) {
        if (
          inputSchema.properties[key].hidden &&
          inputSchema.properties[key].const
        ) {
          data[key] = inputSchema.properties[key].const;
        }
      }
    }
    return data;
  };

  const presetActivityData = (inputSchema) => {
    const data = {};
    if (inputSchema && Object.keys(inputSchema).length) {
      for (const key in inputSchema.properties) {
        if (inputSchema.properties[key].const) {
          data[key] = inputSchema.properties[key].const;
        }
      }
    }
    return data;
  };

  const handleAddActivities = (activityDefinitionArray) => {
    const existingCount = campaignState?.activities?.length || 0;
    let addedIndex = 0;
    return activityDefinitionArray
      .map((activityDefinition) => {
        if (!activityDefinition) {
          return null; // Skip if activityDefinition is undefined
        }

        const formatted = {
          ...formatActivity(activityDefinition),
          outcomes: formatOutcomes(activityDefinition),
        };
        // Assign correct sequence_order accounting for existing activities
        formatted.activityData = {
          ...formatted.activityData,
          sequence_order: existingCount + addedIndex + 1,
        };
        addedIndex++;
        return formatted;
      })
      .filter(Boolean); // Filter out any null values
  };

  const prefilDefaultValuesInRuleData = (rule) => {
    const prefilledValues = {};

    // Iterate over the required properties in the schema
    if (rule?.inputSchema?.required) {
      rule.inputSchema.required.forEach((propertyKey) => {
        const propertySchema = rule.inputSchema?.properties[propertyKey];

        if (propertySchema) {
          // Check if the property has a default value
          if (propertySchema.default !== undefined) {
            prefilledValues[propertyKey] = propertySchema.default;
          } else if (propertySchema.type === "object" && propertySchema.properties) {
            // Recursively prefill nested objects
            const nestedPrefilled = prefilDefaultValuesInRuleData({
              inputSchema: propertySchema,
            });
            if (Object.keys(nestedPrefilled).length > 0) {
              prefilledValues[propertyKey] = nestedPrefilled;
            }
          }
        }
      });
    }

    // Return prefilled values if at least one default value exists, otherwise return {}
    return Object.keys(prefilledValues).length > 0 ? prefilledValues : {};
  };

  const formatActivity = (activityDefinition) => {
    const hasActivityDataSchemas =
      Object.keys(activityDefinition?.inputSchema || {}).length > 0 &&
      Object.keys(activityDefinition?.uiSchema || {}).length > 0;
    return {
      id: crypto.randomUUID(),
      name: activityDefinition.name,
      description: activityDefinition.description,
      inputSchema: activityDefinition.inputSchema,
      activityDefinition: activityDefinition,
      activityRules: formatActivityRules(activityDefinition),
      activityData: {
        ...(hasActivityDataSchemas
          ? presetActivityData(activityDefinition.inputSchema)
          : {}),
        sequence_order: (campaignState?.activities?.length || 0) + 1,
      },
    };
  };

  const formatActivityRules = (activityDefinition) => {
    return categoryActivityRulesSetup
      ? activityDefinition.rulesConfiguration?.rules
          ?.filter((rule) => rule.minRequired > 0 && !rule.rule.hidden)
          .map((rule) => ({
            ruleDefinition: rule.rule,
            ruleData: prefilDefaultValuesInRuleData(rule.rule),
          })) || []
      : activityDefinition.rules
          ?.filter((rule) => activityDefinition.requiredRules?.includes(rule.slug))
          .map((rule) => ({
            ruleDefinition: rule,
            ruleData: {},
          })) || [];
  };

  const formatOutcomes = (activityDefinition) => {
    return (activityDefinition?.requiredOutcomes || [])
      .map((slug) => {
        const outcome = activityDefinition.outcomes?.find(
          (outcome) => outcome.slug === slug
        );
        if (!outcome) return null;

        return {
          ...outcome,
          id: outcome.id,
          outcomeDefinition: outcome,
          outcomeRules: formatOutcomeRules(outcome),
          outcomeData: { ...presetJSONData(outcome.inputSchema) },
        };
      })
      .filter(Boolean); // Filter out any null outcomes
  };

  const formatOutcomeRules = (outcome) => {
    return (
      (outcome?.rules || [])
        .filter((rule) => outcome.requiredRules?.includes(rule.slug))
        .map((rule) => ({
          ruleDefinition: rule,
          ruleData: {},
        })) || []
    );
  };

  const handleUpdateActivity = (activity) => {
    setCampaignState((prev) => ({
      ...prev,
      activities: prev.activities.map((prevActivity) =>
        prevActivity.activityDefinition.id === activity.activityDefinition.id
          ? activity
          : prevActivity
      ),
    }));
  };

  const setCampaignPresets = (campaignTypeConfig) => {
    const requirement = campaignTypeConfig?.requirement || {};
    const campaignObject = createCampaignObject(campaignTypeConfig);

    if (Object.entries(requirement).length) {
      campaignObject.activities = setPresetActivities(requirement);
      campaignObject.campaignRules = setPresetCampaignRules(requirement);
      campaignObject.outcomes = setPresetCampaignOutcomes(requirement);
    }

    setCampaignState(campaignObject);
  };

  const createCampaignObject = (campaignTypeConfig) => {
    const contentDefinitionSlug = campaignTypeConfig.steps
      ?.find((step) => step.slug === "generic-setup")
      ?.values?.find((value) => value.type === "content")?.definition;

    return {
      type: campaignTypeConfig?.type,
      id: crypto.randomUUID(),
      activities: [],
      outcomes: [],
      segments: [],
      excludeSegments: [],
      campaignRules: [],
      campaignContent: {
        contentDefinitionSlug,
        contentData: {},
      },
      endless: false,
    };
  };

  const setPresetActivities = (requirement) => {
    const getPresetActivities = requirement.requiredActivities.map((slug) =>
      requirement.possibleActivities?.find(
        (possibleActivity) => possibleActivity.slug === slug
      )
    );
    return handleAddActivities(getPresetActivities);
  };

  const setPresetCampaignRules = (requirement) => {
    return requirement?.possibleRules
      ?.filter((rule) => requirement?.requiredRules?.indexOf(rule.slug) > -1)
      ?.map((rule) => ({
        ruleDefinition: { ...rule },
        ruleData: prefilDefaultValuesInRuleData(rule),
      }));
  };

  const setPresetCampaignOutcomes = (requirement) => {
    const presetSlugs = ["direct-rewards", "reward-allocation-outcome"];
    if (presetSlugs.includes(requirement.requiredOutcomes?.[0])) {
      return requirement?.possibleOutcomes
        ?.filter(
          (outcome) => requirement?.requiredOutcomes?.indexOf(outcome.slug) > -1
        )
        ?.map((outcome) => {
          const base = {
            outcomeDefinition: { ...outcome },
            outcomeRules: [],
            outcomeData: {},
          };
          if (outcome.slug === "reward-allocation-outcome") {
            base.id = crypto.randomUUID();
            base.outcomeData = {
              reward_allocation_config: {
                distribution_strategy: "",
                rollover_enabled: false,
                pool_user_win_limits: {},
                rewards: [],
              },
            };
          }
          return base;
        });
    }
    return [];
  };

  const handleAddOutcome = (outcomes, rewardGroupId) => {
    setCampaignState((prev) => {
      const stateObj = {
        ...prev,
        outcomes: [...outcomes],
      };
      if (rewardGroupId) {
        const prevDeletedRewardGroups = prev?.deletedRewardGroups || [];
        stateObj.deletedRewardGroups = [...prevDeletedRewardGroups, rewardGroupId];
        stateObj.deletedRewardGroups = [...new Set(stateObj.deletedRewardGroups)];
      }
      return stateObj;
    });
  };

  const handleAddRules = (rules) => {
    setCampaignState((prev) => ({
      ...prev,
      campaignRules: [...rules],
    }));
  };

  const handleAddRule = (rule) => {
    setCampaignState((prev) => ({
      ...prev,
      campaignRules: [...prev.campaignRules, rule],
    }));
  };

  const toggleCampaignActiveInactive = () => {
    setCampaignState((prev) => ({
      ...prev,
      paused: prev.campaignStatus === "active",
    }));
  };

  return {
    handleCampaignSetup,
    handleAddRule,
    handleCampaignContent,
    setCampaignPresets,
    handleAddActivities,
    handleUpdateActivity,
    handleAddOutcome,
    handleAddRules,
    prefilDefaultValuesInRuleData,
    toggleCampaignActiveInactive,
    handleTermsContent,
  };
};
